import { apiClient } from '../../lib/apiClient';
import type {
  BusinessRead,
  BusinessUpdate,
  OnboardingCreate,
  OnboardingParseRead,
  OnboardingParseRequest,
  OnboardingRead,
  ProfileRead,
  ProfileUpdate,
} from '../../types/api';

/**
 * POST /api/v1/me/onboarding/parse - extracts brief fields from pasted text.
 * Deterministic and free; nothing is stored.
 */
export function parseBrief(text: string, signal?: AbortSignal): Promise<OnboardingParseRead> {
  const body: OnboardingParseRequest = { text };
  return apiClient.post<OnboardingParseRead>('/api/v1/me/onboarding/parse', { body, signal });
}

/** GET /api/v1/me/profile — the signed-in user's profile and onboarding flag. */
export function fetchProfile(signal?: AbortSignal): Promise<ProfileRead> {
  return apiClient.get<ProfileRead>('/api/v1/me/profile', { signal });
}

/** PUT /api/v1/me/profile — partial update; onboarding answers stay editable. */
export function updateProfile(body: ProfileUpdate): Promise<ProfileRead> {
  return apiClient.put<ProfileRead>('/api/v1/me/profile', { body });
}

/**
 * POST /api/v1/me/onboarding — creates the business and flips
 * `onboarding_completed`. Returns 409 `ONBOARDING_ALREADY_COMPLETED` on repeat.
 */
export function submitOnboarding(body: OnboardingCreate): Promise<OnboardingRead> {
  return apiClient.post<OnboardingRead>('/api/v1/me/onboarding', { body });
}

/**
 * GET /api/v1/me/businesses — every brand this user belongs to, oldest first.
 * Empty before onboarding completes.
 */
export function fetchBusinesses(signal?: AbortSignal): Promise<BusinessRead[]> {
  return apiClient.get<BusinessRead[]>('/api/v1/me/businesses', { signal });
}

/**
 * POST /api/v1/me/businesses — adds a brand to an onboarded account. Returns
 * 409 `BUSINESS_LIMIT_REACHED` at the cap.
 */
export function createBusiness(body: OnboardingCreate): Promise<BusinessRead> {
  return apiClient.post<BusinessRead>('/api/v1/me/businesses', { body });
}

/** PUT /api/v1/businesses/{id} — partial update of the brand's name and brief. Owner only. */
export function updateBusiness(businessId: string, body: BusinessUpdate): Promise<BusinessRead> {
  return apiClient.put<BusinessRead>(`/api/v1/businesses/${businessId}`, { body });
}

/**
 * DELETE /api/v1/businesses/{id} — the brand and everything scoped to it.
 * Owner only, 204, not undoable. Deleting the last brand reopens onboarding.
 */
export function deleteBusiness(businessId: string): Promise<void> {
  return apiClient.delete<void>(`/api/v1/businesses/${businessId}`);
}

/**
 * DELETE /api/v1/me — the account and every brand it owns. 204, not undoable.
 * The caller signs out afterwards; the server-side user no longer exists.
 */
export function deleteAccount(): Promise<void> {
  return apiClient.delete<void>('/api/v1/me');
}
